// Moved by `git mv` from
// `src/hooks/mutation/integration/ehr-launch/useMutateConnectIntegrationEhrLaunch.ts`,
// which survives as a delegate — so `git log --follow` on THIS path stops at the
// move and the pre-move history is `git log --follow` on the source path. It had
// no co-located suite; the suite beside this file was written at carve time.
//
// TIER T2 (PORTS). The record bucketed this file as STORE-TETHERED and that
// classification has EXPIRED: at this head it names no `@/store` and no
// `react-redux` — the analytics leg is a plain emitter module. What is left is a
// success sequence of four app statements and an error branch that writes a
// jotai app atom, all of which stay in the delegate.
//
// THE WHOLE `onSuccess` SEQUENCE CROSSES AS ONE PORT, and that is deliberate
// rather than lazy. It is four statements in a fixed order — invalidate
// `[GET_USER_EHR]`, invalidate the launch profiles, emit `connect_integration`,
// toast — and the first two are AWAITED SEQUENTIALLY. This door owns the second
// identity and could invalidate it here, which would split the sequence across
// the boundary and reorder the awaits against the emit. One port keeps the order
// a property of one file.
import { useMutation } from '@tanstack/react-query';

import { ehrMutationKeys } from '../../contracts/queryKeys';
import type { CreateUserEHRLaunchProfileAPIV2Request } from '../../contracts/types';
import { connectIntegrationEhrLaunch } from '../api/launch-profile';

type Params = {
  /**
   * Fired where the two invalidations, the analytics emit and the success toast
   * fired, with the payload the request was made with. AWAITED, because the
   * pre-carve `onSuccess` was `async` and awaited both invalidations before
   * returning.
   */
  onIntegrationConnected?: (
    payload: CreateUserEHRLaunchProfileAPIV2Request
  ) => Promise<void> | void;
  /** Fired for every rejection; the delegate reads the error body and branches. */
  onConnectIntegrationError?: (error: Error) => void;
};

/**
 * Creates the user's EHR-launch profile — the v2 "connect this integration"
 * write (`POST /ehr-launch/onboarding/user-ehr-launch-profile/v2`).
 */
export function useMutateConnectIntegrationEhrLaunch({
  onIntegrationConnected,
  onConnectIntegrationError,
}: Params = {}) {
  return useMutation({
    mutationKey: ehrMutationKeys.connectIntegrationEhrLaunch(),
    mutationFn: async (payload: CreateUserEHRLaunchProfileAPIV2Request) => {
      return await connectIntegrationEhrLaunch(payload);
    },
    onSuccess: async (_, payload) => {
      await onIntegrationConnected?.(payload);
    },
    onError: (error) => {
      onConnectIntegrationError?.(error);
    },
  });
}
